import { createStyles, makeStyles } from "@material-ui/core";
import {
    Button,
    DisplayField,
    Layout,
    LayoutCell,
    LayoutContainer,
    PencilIcon,
    RelodeTheme,
    useDeviceSize,
} from "@relode/core";
import { JobType } from "@relode/core/domain";
import { format } from "date-fns";
import * as React from "react";

import { useCandidateContext } from "../../../features/candidates/_infra";
import {
    CandidateDetails,
    CandidateHiredDetails,
    HiredCandidate,
    HiredCandidateToUpdate,
} from "../../types";
import { HiredCandidateDialog, SetHiredCandidateStatus } from "./";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                [theme.breakpoints.down("sm")]: {
                    display: "flex",
                    flexDirection: "column",
                },
            },
            section: {
                marginTop: theme.spacing(3),
            },
            label: {
                fontSize: 16,
                textTransform: "none",
                marginRight: theme.spacing(20),
            },
            editButton: {
                marginTop: theme.spacing(2),
                marginRight: theme.spacing(4),
            },
            container: {
                [theme.breakpoints.up("md")]: {
                    margin: theme.spacing(0, 3),
                },
            },
            editIcon: {
                marginRight: theme.spacing(1),
            },
        }),
    { name: "CandidateHireInfo" }
);

interface CandidateHireInfoProps {
    hiredInfo: CandidateHiredDetails;
    candidateJobType: JobType;
    candidate: CandidateDetails;
}
export const CandidateHiredInfo = (props: CandidateHireInfoProps) => {
    const classes = useStyles({});
    const device = useDeviceSize();
    const { hiredInfo, candidateJobType, candidate } = props;
    const [isHireModalOpen, setIsHireModalOpen] = React.useState(false);

    const {
        actions: { editHireDetails },
    } = useCandidateContext();

    const handleEditDialogOpen = () => {
        setIsHireModalOpen(true);
    };

    const handleEditDialogClose = () => {
        setIsHireModalOpen(false);
    };

    const handleHireCandidate = async (
        hiredCandidateToUpdate: HiredCandidateToUpdate,
        setHiredCandidateStatus: SetHiredCandidateStatus
    ) => {
        await editHireDetails(
            candidate.job.jobId,
            candidate.highlights.leadId,
            hiredCandidateToUpdate,
            setHiredCandidateStatus
        );
    };

    const getDefaultHiredCandidate = (): HiredCandidate => {
        const {
            hiredDetails: {
                salaryTypeId,
                salaryInUSDollars,
                startDate,
                endDate,
                hoursPerWeek,
            },
            candidateConcurrencyToken,
        } = candidate;

        const result = {
            hiredJobSalaryInUSDollars: salaryInUSDollars,
            hiredStartDate: startDate,
            hiredEndDate: endDate,
            hiredHoursPerWeek: hoursPerWeek,
            hiredJobSalaryTypeId: salaryTypeId,
            candidateConcurrencyToken: candidateConcurrencyToken,
        };
        return result;
    };

    const renderStartEndDate = () => {
        if (JobType.isContractOrLocum(candidateJobType.jobTypeId)) {
            return hiredInfo.endDate ? (
                <DisplayField
                    label="start - end"
                    value={`${format(hiredInfo.startDate, "P")} - ${format(
                        hiredInfo.endDate,
                        "P"
                    )}`}
                />
            ) : null;
        }

        if (JobType.isFullTimeOrPartTime(candidateJobType.jobTypeId)) {
            return hiredInfo.startDate ? (
                <DisplayField
                    label="start date"
                    value={hiredInfo.startDate}
                    mode="date"
                />
            ) : null;
        }
    };

    if (!hiredInfo) return null;

    return (
        <>
            <LayoutContainer>
                <Layout mode="50/50">
                    {candidate.canEditHire ? (
                        <LayoutCell columns={2}>
                            <Button
                                size="small"
                                color="default"
                                variant="contained"
                                onClick={handleEditDialogOpen}
                                title="edit hire details"
                                className={classes.editButton}
                                fullWidth={device === "mobile"}
                                startIcon={<PencilIcon />}
                                data-cy="editHire-button"
                            >
                                hire
                            </Button>
                        </LayoutCell>
                    ) : null}
                    {JobType.isContractOrLocum(candidateJobType.jobTypeId) ? (
                        <DisplayField
                            label="start - end"
                            value={`${format(hiredInfo.startDate, "P")} - ${
                                hiredInfo.endDate
                                    ? format(hiredInfo.endDate, "P")
                                    : "n/a"
                            }`}
                        />
                    ) : null}
                    {JobType.isFullTimeOrPartTime(
                        candidateJobType.jobTypeId
                    ) ? (
                        <DisplayField
                            label="start date"
                            value={hiredInfo.startDate}
                            mode="date"
                        />
                    ) : null}

                    <DisplayField
                        label="duration"
                        value={hiredInfo.duration}
                        hideWhenEmpty
                    />
                    <DisplayField
                        label={
                            hiredInfo.salaryIsRate
                                ? "hourly bill rate"
                                : "starting salary"
                        }
                        value={hiredInfo.salaryDescription}
                        hidden={!hiredInfo.canViewSalary}
                    />
                    <DisplayField
                        label="termination date"
                        value={hiredInfo.terminationDate}
                        mode="date"
                        hidden={!hiredInfo.terminationDate}
                    />
                    <DisplayField
                        label="hours per week"
                        value={hiredInfo.hoursPerWeek}
                        hidden={!hiredInfo.canViewSalary}
                    />
                    {JobType.isFullTimeOrPartTime(
                        candidateJobType.jobTypeId
                    ) ? (
                        <DisplayField
                            label="success fee"
                            value={hiredInfo.successFeeDescription}
                            hidden={!hiredInfo.canViewSalary}
                        />
                    ) : null}
                </Layout>
            </LayoutContainer>
            {isHireModalOpen ? (
                <HiredCandidateDialog
                    candidate={getDefaultHiredCandidate()}
                    candidateName={candidate.highlights.fullName}
                    job={candidate.job}
                    onSubmit={handleHireCandidate}
                    onComplete={handleEditDialogClose}
                    onClose={handleEditDialogClose}
                />
            ) : null}
        </>
    );
};
